/**
 * Edit Batch Dialog Component
 * Dialog for editing batch information like storage location, notes, etc.
 *
 * @author SAUBHAGYA Development Team
 * @since US-BATCH-006
 */

import { useState, useEffect } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import type { BatchResponse } from '@/types/inventory';
import { AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

// ============================================
// FORM VALIDATION SCHEMA
// ============================================

const editBatchSchema = z.object({
  storageLocation: z
    .string()
    .min(1, 'Storage location is required')
    .min(3, 'Storage location must be at least 3 characters')
    .max(200, 'Storage location cannot exceed 200 characters'),
  remarks: z
    .string()
    .max(1000, 'Remarks cannot exceed 1000 characters')
    .optional()
    .nullable(),
});

type EditBatchFormValues = z.infer<typeof editBatchSchema>;

// ============================================
// COMPONENT PROPS
// ============================================

interface EditBatchDialogProps {
  batch: BatchResponse;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (batch: BatchResponse) => void;
}

// ============================================
// COMPONENT
// ============================================

export function EditBatchDialog({
  batch,
  open,
  onOpenChange,
  onSuccess,
}: EditBatchDialogProps) {
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const form = useForm<EditBatchFormValues>({
    resolver: zodResolver(editBatchSchema),
    defaultValues: {
      storageLocation: batch.storageLocation || '',
      remarks: '',
    },
  });

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      form.reset({
        storageLocation: batch.storageLocation || '',
        remarks: '',
      });
      setError(null);
    }
  }, [open, batch.id, batch.storageLocation]);

  // Handle form submission
  async function onSubmit(values: EditBatchFormValues) {
    try {
      setIsUpdating(true);
      setError(null);

      // TODO: Implement actual API call to update batch
      // For now, just show success message
      toast.success('Batch Updated', {
        description: `Batch ${batch.batchNumber} has been updated`,
      });

      onOpenChange(false);
      if (onSuccess) {
        onSuccess(batch);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update batch';
      setError(errorMessage);
      toast.error('Update Failed', {
        description: errorMessage,
      });
    } finally {
      setIsUpdating(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit Batch</DialogTitle>
          <DialogDescription>
            Edit batch information for {batch.batchNumber}
          </DialogDescription>
        </DialogHeader>

        {/* Batch Information Display */}
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-sm text-gray-600">Batch Number</div>
              <div className="font-semibold">{batch.batchNumber}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Product Type</div>
              <Badge variant="outline">{batch.productType}</Badge>
            </div>
            <div>
              <div className="text-sm text-gray-600">Quality Grade</div>
              <Badge>Grade {batch.qualityGrade}</Badge>
            </div>
            <div>
              <div className="text-sm text-gray-600">Status</div>
              <Badge variant="secondary">{batch.batchStatus}</Badge>
            </div>
          </div>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Storage Location */}
            <FormField
              control={form.control}
              name="storageLocation"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Storage Location *</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="e.g., Plant-1/Tank-3A"
                      disabled={isUpdating}
                      {...field}
                      aria-label="Storage Location"
                    />
                  </FormControl>
                  <FormDescription>
                    Physical location where batch is stored
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Remarks */}
            <FormField
              control={form.control}
              name="remarks"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Remarks (Optional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Additional notes about this batch..."
                      className="min-h-[80px] resize-none"
                      disabled={isUpdating}
                      {...field}
                      value={field.value || ''}
                      aria-label="Remarks"
                    />
                  </FormControl>
                  <FormDescription>
                    {field.value?.length || 0}/1000 characters (optional)
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Error Display */}
            {error && (
              <Alert className="border-red-300 bg-red-50">
                <AlertTriangle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-800">{error}</AlertDescription>
              </Alert>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isUpdating}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isUpdating}
              >
                {isUpdating ? 'Updating...' : 'Update Batch'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
